import { Button, Icon, Table, Typography, Tooltip } from 'antd'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useMemo, useEffect, useCallback, forwardRef } from 'react'
import { createUseStyles, useTheme } from 'react-jss'
import { Link } from 'react-router-dom'
import { formatDOB } from '../../lib/datetime'
import { reportShowPath, reportEditPath } from '../../lib/reports'
import { PAGE_SIZE_OPTIONS, PAGE_SIZE } from '../../lib/constants'
import {
  useIsAcknowledged,
  useReports,
  useReportsPagination,
} from '../../redux/slices/reports'
import {
  useCanSign,
  useFeatureEnabled,
  useIsAdmin,
} from '../../redux/slices/session'
import { toPath } from '../../routes'
import useQueryParams from '../../hooks/useQueryParams'
import useLocalStorage from '../../hooks/useLocalStorage'
import DiseasesTags from '../Common/DiseasesTags'
import DataExtractionIcon from '../Common/DataExtractionIcon'
import DataExtractionReference from '../Common/DataExtractionReference'
import Acknowledge from './Acknowledge'

const { Text } = Typography

const useStyles = createUseStyles({
  present: {
    color: 'green',
  },
  notPresent: {
    color: 'orange',
  },
  criticalIcon: {
    color: 'red',
    paddingTop: '0.3em',
  },
  parsingReference: {
    padding: '1rem 0',
    display: 'flex',
    flexDirection: 'row-reverse',
  },
})

const BASE_COLUMNS = [
  {
    title: 'MRN',
    dataIndex: 'patient.practiceMrn',
    key: 'MRN',
  },
  {
    title: 'DOB',
    dataIndex: 'patient.birthDate',
    key: 'dob',
    render: formatDOB,
  },
  {
    title: 'Stroke Prevention',
    key: 'diseases',
    render(text, report) {
      return <DiseasesTags patientSummary={report.patient} />
    },
  },
  {
    title: 'Device Type',
    dataIndex: 'device.deviceModel.deviceType.name',
    key: 'deviceType',
  },
  {
    title: 'CPT Code',
    dataIndex: 'cptCode',
    key: 'cptCode',
  },
  {
    title: 'ICD-10 Code',
    dataIndex: 'icd10Code',
    key: 'icd10Code',
  },
  {
    title: 'Report Date',
    key: 'date',
    render(text, report) {
      return (
        <span>
          <Text>{moment(report.reportDate).format('ll')}</Text>
        </span>
      )
    },
  },
]

const ADMIN_COLUMNS = [
  {
    title: 'Parsing',
    key: 'pdfExtraction',
    render(text, report) {
      return (
        <DataExtractionIcon
          extractionStatus={report.pdfExtraction?.status}
          showLabel={false}
        />
      )
    },
  },
]

const LOCALE = {
  emptyText: 'This cycle doesn’t have the reports ready yet',
}

const Reports = forwardRef((props, ref) => {
  const {
    cycleId,
    practiceId,
    onPageChange,
    page,
    filters,
    selected,
    onSelect,
    fetchReports,
    pendingFetch,
  } = props

  const theme = useTheme()
  const classes = useStyles({ theme })
  const reports = useReports(cycleId)
  const { total } = useReportsPagination(cycleId)
  const isAdmin = useIsAdmin()
  const isAcknowledged = useIsAcknowledged(cycleId)
  const [query, setConditions] = useQueryParams({})
  const canSign = useCanSign()
  const batchEnabled = useFeatureEnabled('report_batch_signatures')
  const rowSelection =
    canSign && batchEnabled
      ? {
          selectedRowKeys: selected,
          onChange: onSelect,
          getCheckboxProps: record => ({
            disabled: !!record.signedAt,
          }),
        }
      : null
  // eslint-disable-next-line no-unused-vars
  const [localStoragePageSize, setPageSize] = useLocalStorage(
    'pageSize',
    PAGE_SIZE,
  )
  const pageSize = query.perPage || localStoragePageSize

  useEffect(() => {
    fetchReports(practiceId, cycleId, page, filters, pageSize)
  }, [fetchReports, practiceId, cycleId, page, filters, pageSize])

  const shouldSign = useCallback(
    row => {
      return (
        row.hasBiobridgeReport && canSign && !row.signedAt && !row.isArchived
      )
    },
    [canSign],
  )

  const columns = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'critical',
        key: 'critical',
        align: 'center',
        render(_text, { critical }) {
          return (
            critical && (
              <Tooltip title="Requires Attention">
                <Icon
                  type="exclamation-circle"
                  className={classes.criticalIcon}
                />
              </Tooltip>
            )
          )
        },
      },
      {
        title: 'Full Name',
        key: 'id',
        render: (_, row) => {
          if (!row?.patient) return null
          return (
            <>
              <Link
                to={toPath.patients.patient({ id: row.patient.id })}
                style={{ whiteSpace: 'nowrap', marginRight: 5 }}
              >
                <Tooltip title="View Patient">
                  {`${row.patient.lastName}, ${row.patient.firstName}`}
                </Tooltip>
              </Link>
              <Link
                to={toPath.patients.patient.edit({ id: row.patient.id })}
                title="Edit Patient"
              >
                <Tooltip title="Edit Patient">
                  <Icon type="edit" />
                </Tooltip>
              </Link>
            </>
          )
        },
      },
      ...BASE_COLUMNS,
      ...(isAdmin ? ADMIN_COLUMNS : []),
      {
        title: 'Signed',
        dataIndex: 'signed',
        key: 'signedAt',
        align: 'center',
        render(_text, { signedAt }) {
          return (
            <Icon
              type={`${signedAt ? 'check' : 'close'}-circle`}
              className={classes[signedAt ? 'present' : 'notPresent']}
            />
          )
        },
      },
      {
        title: 'PDF',
        dataIndex: 'hasBiobridgeReport',
        key: 'hasBiobridgeReport',
        align: 'center',
        render(_text, { hasBiobridgeReport }) {
          return (
            <Icon
              type={`${hasBiobridgeReport ? 'check' : 'close'}-circle`}
              className={classes[hasBiobridgeReport ? 'present' : 'notPresent']}
            />
          )
        },
      },
      {
        title: 'Full',
        dataIndex: 'hasDeviceReport',
        key: 'hasDeviceReport',
        align: 'center',
        render(_text, { hasDeviceReport }) {
          return (
            <Icon
              type={`${hasDeviceReport ? 'check' : 'close'}-circle`}
              className={classes[hasDeviceReport ? 'present' : 'notPresent']}
            />
          )
        },
      },
      {
        title: 'Actions',
        key: 'actions',
        render(_text, report) {
          return (
            <div className={classes.actions}>
              {!report.hasDeviceReport && (
                <Link to={reportEditPath(report)}>
                  <Tooltip title="Add Device Report">
                    <Button type="link" icon="plus" size="small" />
                  </Tooltip>
                </Link>
              )}
              {shouldSign(report) && (
                <Link to={reportShowPath(report)}>
                  <Tooltip title="Sign Report">
                    <Button type="link" icon="edit" size="small" />
                  </Tooltip>
                </Link>
              )}
              {report.hasDeviceReport && (
                <>
                  <Link to={reportShowPath(report)}>
                    <Tooltip title="View Report">
                      <Button type="link" icon="file-search" size="small" />
                    </Tooltip>
                  </Link>
                  <Link to={reportEditPath(report)}>
                    <Tooltip title="Edit Report">
                      <Button type="link" icon="edit" size="small" />
                    </Tooltip>
                  </Link>
                </>
              )}
            </div>
          )
        },
      },
    ],
    [classes, shouldSign, isAdmin],
  )

  const onShowSizeChange = useCallback(
    (current, size) => {
      setConditions({ ...query, cycleId, page: current, perPage: size })
    },
    [cycleId, query, setConditions],
  )

  if (!pendingFetch && !isAdmin && !isAcknowledged && reports.length) {
    return <Acknowledge practiceId={practiceId} cycleId={cycleId} />
  }

  return (
    <>
      {isAdmin && (
        <div className={classes.parsingReference}>
          <DataExtractionReference />
        </div>
      )}

      <Table
        columns={columns}
        dataSource={reports}
        rowKey="id"
        loading={pendingFetch}
        locale={LOCALE}
        size="middle"
        rowSelection={rowSelection}
        pagination={{
          current: page,
          pageSize: Number(pageSize),
          onChange: onPageChange,
          total,
          showSizeChanger: true,
          onShowSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />
    </>
  )
})

Reports.propTypes = {
  cycleId: PropTypes.string.isRequired,
  practiceId: PropTypes.string.isRequired,
  onPageChange: PropTypes.func.isRequired,
  fetchReports: PropTypes.func.isRequired,
  pendingFetch: PropTypes.bool,
  page: PropTypes.number,
  filters: PropTypes.shape({}),
  onSelect: PropTypes.func,
  selected: PropTypes.arrayOf(PropTypes.string),
}

Reports.defaultProps = {
  page: 1,
  filters: {},
  onSelect: () => {},
  selected: [],
  pendingFetch: false,
}

export default Reports
